import React from 'react';
import { get, omit, flowRight as compose } from 'lodash';
import PropTypes from 'prop-types';
import { graphql, withApollo } from '@apollo/client/react/hoc';
import * as Sentry from '@sentry/nextjs';
import RedirectSignIn from '../../components/RedirectSignIn';
import Error from '../../pages/_error';
import { ME_QUERY } from './gql';
import { getAuthStore, setAuthTokens, GQLLogin, GQLLogout, GQLChangeOrganization } from './service';
import { UserCtx, UserCtxData } from '../../contexts/UserCtx';

export const withAuthPropType = PropTypes.shape({
  loginAuthToken: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  changeOrganization: PropTypes.func.isRequired,
  authenticated: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  authError: PropTypes.string,
  me: PropTypes.shape({})
});

const withAuth = (options) => (BaseComponent) => {
  class ComponentWithAuth extends React.Component {
    constructor(props) {
      super(props);

      this.buildProps = this.buildProps.bind(this);
      this.login = this.login.bind(this);
      this.loginAuthToken = this.loginAuthToken.bind(this);
      this.logout = this.logout.bind(this);
      this.changeOrganization = this.changeOrganization.bind(this);
      this.state = { authError: null };
    }

    async componentDidMount() {
      this.store = getAuthStore();
    }

    async loginAuthToken(variables) {
      const { client, reload } = this.props;
      setAuthTokens(client, this.store, variables);
      await reload();
    }

    async login(variables) {
      const { client } = this.props;
      this.setState({ authError: null });
      const authError = await GQLLogin(client, this.store, variables);
      this.setState({ authError });
      if (authError) {
        throw authError;
      }

      // After successful login, refetch the ME_QUERY to get updated user data
      const { data } = await client.query({
        query: ME_QUERY,
        fetchPolicy: 'network-only'
      });

      // Set Sentry user context if we have user data
      if (data?.me) {
        Sentry.setUser({
          id: data.me.userId,
          email: data.me.emailAddress,
          username: `${data.me.firstName} ${data.me.lastName}`.trim(),
          organization: data.me?.activeOrganization?.name || undefined
        });
      }
    }

    async logout() {
      const { client } = this.props;
      // Clear Sentry user context on logout
      Sentry.setUser(null);
      await GQLLogout(client, this.store);
    }

    async changeOrganization(variables) {
      const { client } = this.props;
      await GQLChangeOrganization(client, this.store, variables);
    }

    buildProps() {
      const { me, authenticated, loading, reload } = this.props;
      const hocProps = {
        ...this.state,
        me,
        authenticated,
        loading,
        loginAuthToken: this.loginAuthToken,
        login: this.login,
        logout: this.logout,
        changeOrganization: this.changeOrganization,
        reload
      };
      return hocProps;
    }

    render() {
      const { authenticated, loading, me } = this.props;
      const requireAuthentication = get(options, 'requireAuthentication', false);
      const userRole = get(options, 'userRole');

      // The "requireAuthentication" option sets up a gate so that the component will
      // not render unless the user is authenticated
      if (requireAuthentication) {
        // While we figure out if the user is authenticated render nothing
        if (loading) {
          return null;
        }

        // If we are done loading and the user is not authenticated
        if (!authenticated) {
          return <RedirectSignIn method="replace" />;
        }

        // if we require the user to have a specific role check, and display an error if the user doesn't have that role.
        if (authenticated && userRole) {
          const currentRoles = get(me, 'activeOrganization.roles', null);
          const userHasRequiredRole = currentRoles.includes(userRole);
          if (!userHasRequiredRole) {
            return <Error statusCode={403} statusMessage={`Missing role: ${userRole}`} />;
          }
        }
      }

      // One might extend UserCtxData.fromMe to include other props that are passed down using the old method
      const userCtx = UserCtxData.fromMe(me);

      // These props are added from the grapql query query but don't need to be passed down
      const blackListProps = ['client', 'me', 'authenticated', 'loading', 'reload', 'error'];
      const passThroughProps = omit(this.props, blackListProps);

      return (
        <UserCtx.Provider value={userCtx}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <BaseComponent auth={this.buildProps()} {...passThroughProps} />
        </UserCtx.Provider>
      );
    }
  }
  ComponentWithAuth.displayName = `withAuth(${BaseComponent.displayName || BaseComponent.name})`;

  ComponentWithAuth.propTypes = {
    client: PropTypes.shape({
      query: PropTypes.func
    }),
    me: PropTypes.shape({}),
    authenticated: PropTypes.bool,
    loading: PropTypes.bool,
    reload: PropTypes.func
  };

  ComponentWithAuth.defaultProps = {
    client: {
      query: () => {}
    },
    me: {},
    authenticated: false,
    loading: false,
    reload: () => {}
  };

  return compose(
    withApollo,
    graphql(ME_QUERY, {
      options: () => ({
        errorPolicy: 'all',
        ssr: false
      }),
      props: ({ data }) => {
        const me = get(data, 'me', null);
        const error = get(data, 'error', null);
        const userId = get(me, 'userId', null);
        const isLodgeLinkAdmin = get(me, 'admin', null);
        const authenticated = userId !== null && isLodgeLinkAdmin;

        return {
          me,
          authenticated,
          error,
          loading: data.loading,
          reload: data.refetch
        };
      }
    })
  )(ComponentWithAuth);
};

export default withAuth;
